import { json } from '@codemirror/lang-json';
import { okaidiaInit } from '@uiw/codemirror-theme-okaidia';
import CodeMirror from '@uiw/react-codemirror';
import { useEffect, useMemo, useState } from 'react';

import { FieldLabel } from '~/components/form';
import {
  Button,
  Modal,
  ModalActions,
  PanelTable,
  Tabs,
  type PanelTableColumn,
} from '~/components/ui';
import { themeColor } from '~/lib/theme-colors';
import type {
  PlacementComponentPropDefinition,
  PlacementComponentVersionOption,
} from '~/modules/query/ai/types';

export type PlacementTestDataTab = 'context' | 'props';

interface TestDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab: PlacementTestDataTab;
  sampleContext: string;
  sampleProps: string;
  onContextChange: (value: string) => void;
  onPropsChange: (value: string) => void;
  componentVersion: PlacementComponentVersionOption | null;
  renderSnippet: string;
}

interface PropRow extends PlacementComponentPropDefinition {
  key: string;
}

const typeOf = (value: unknown): string =>
  Array.isArray(value) ? 'array' : value === null ? 'null' : typeof value;

const formatDefault = (value: unknown): string => {
  if (value === undefined) {
    return '—';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return '—';
  }
};

function rowsFor(version: PlacementComponentVersionOption | null): PropRow[] {
  if (!version) {
    return [];
  }

  const declared = (version.propDefinitions ?? []).filter(
    (definition) => definition.name !== 'onRunAi',
  );

  if (declared.length > 0) {
    return declared.map((definition) => ({
      ...definition,
      key: `${definition.name}-${definition.source}-${definition.sourcePath ?? 'root'}`,
    }));
  }

  return Object.entries(version.defaultProps).map(([name, value]) => ({
    key: `default-${name}`,
    name,
    type: typeOf(value),
    required: false,
    source: 'sampleProps',
    sourcePath: name,
    defaultValue: value,
    description: "Set from the component's default props.",
  }));
}

const COLUMNS: PanelTableColumn<PropRow>[] = [
  {
    header: 'Prop',
    render: (row) => (
      <span className="font-mono text-xs text-gray-800 dark:text-gray-200">{row.name}</span>
    ),
    width: 'w-40',
  },
  { header: 'Type', render: (row) => row.type, width: 'w-24' },
  { header: 'Source', render: (row) => row.source, width: 'w-32' },
  { header: 'Path', render: (row) => row.sourcePath || '—', width: 'w-48' },
  { header: 'Required', render: (row) => (row.required ? 'Yes' : 'No'), width: 'w-24' },
  { header: 'Default', render: (row) => formatDefault(row.defaultValue), width: 'w-40' },
  { header: 'Description', render: (row) => row.description },
];

/** The sample context and props a test run sends, beside the defaults and props the component supports. */
export default function TestDataModal({
  isOpen,
  onClose,
  initialTab,
  sampleContext,
  sampleProps,
  onContextChange,
  onPropsChange,
  componentVersion,
  renderSnippet,
}: TestDataModalProps) {
  const [tab, setTab] = useState<PlacementTestDataTab>(initialTab);
  const extensions = useMemo(() => [json()], []);
  // CodeMirror is a library surface, so its colours come from tokens (DESIGN.md §3.6).
  const theme = useMemo(
    () => okaidiaInit({ settings: { caret: themeColor('gray-300'), fontFamily: 'monospace' } }),
    [],
  );
  const rows = useMemo(() => rowsFor(componentVersion), [componentVersion]);

  useEffect(() => {
    if (isOpen) {
      setTab(initialTab);
    }
  }, [initialTab, isOpen]);

  const defaultContext = JSON.stringify(componentVersion?.defaultContext ?? {}, null, 2);
  const defaultProps = JSON.stringify(componentVersion?.defaultProps ?? {}, null, 2);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xlarge"
      title="Test data"
      description="The context and props a test run sends to the component. They are not saved with the placement."
      testId="ai-placement-test-data-modal"
      footer={<ModalActions onDismiss={onClose} dismissTestId="ai-placement-test-data-close" />}
    >
      <div className="space-y-6">
        <Tabs
          variant="pill"
          ariaLabel="Test data editors"
          idPrefix="ai-placement-test-data"
          activeId={tab}
          onChange={(id) => setTab(id as PlacementTestDataTab)}
          tabs={[
            { id: 'context', label: 'Context' },
            { id: 'props', label: 'Props' },
          ]}
        />

        <div
          role="tabpanel"
          id={`ai-placement-test-data-panel-${tab}`}
          aria-labelledby={`ai-placement-test-data-tab-${tab}`}
          className="grid grid-cols-1 gap-6 lg:grid-cols-2"
        >
          <div>
            <FieldLabel
              labelAction={
                <Button
                  variant="link"
                  onClick={() =>
                    tab === 'context'
                      ? onContextChange(defaultContext)
                      : onPropsChange(defaultProps)
                  }
                  testId="ai-placement-test-data-restore"
                >
                  Restore default
                </Button>
              }
            >
              {tab === 'context' ? 'Sample context (JSON)' : 'Sample props (JSON)'}
            </FieldLabel>
            <div className="overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700">
              <CodeMirror
                aria-label={
                  tab === 'context' ? 'Sample context JSON editor' : 'Sample props JSON editor'
                }
                value={tab === 'context' ? sampleContext : sampleProps}
                height="380px"
                theme={theme}
                extensions={extensions}
                basicSetup={{
                  bracketMatching: true,
                  foldGutter: true,
                  highlightActiveLine: true,
                  lineNumbers: true,
                }}
                onChange={(value) =>
                  tab === 'context' ? onContextChange(value) : onPropsChange(value)
                }
              />
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                {tab === 'context' ? 'Default context' : 'Default props'}
              </p>
              <pre className="max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
                {tab === 'context' ? defaultContext : defaultProps}
              </pre>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Render snippet
              </p>
              <pre className="max-h-32 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 dark:border-gray-800 dark:bg-white/[0.03] dark:text-gray-200">
                {renderSnippet || 'Select a component and version to generate a snippet.'}
              </pre>
              {componentVersion?.releaseNote ? (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  {componentVersion.releaseNote}
                </p>
              ) : null}
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Supported props
              </p>
              <PanelTable
                columns={COLUMNS}
                data={rows}
                getRowId={(row) => row.key}
                emptyMessage="This component version documents no props."
                emptyDescription="Write the test data by hand, or pick another version."
                maxHeight="260px"
                ariaLabel="Supported props"
                testId="ai-placement-test-data-props-table"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
